import React, { FC, useCallback, useMemo } from 'react';
import styled from 'styled-components/native';

import { DEMO_LOGINS, ELoginAs } from '@webtrader/core/constants/account';
import { EDataTestId } from '@webtrader/core/constants/test';
import dynamicSize from '@webtrader/core/dynamic-size';
import { useTranslation } from '@webtrader/core/i18n';
import { ALL_MOBILE_ROUTES } from '@webtrader/core/router/mobile';
import { useCompany } from '@webtrader/core/services/company';
import { validateCompanyAccess } from '@webtrader/core/validators/company';

import Text from '@/components/Text';
import { navigate } from '@/utils/navigation';
import { openInAppBrowser } from '@/utils/web';

interface IProps {
  selectedTab: ELoginAs;
  registrationUrl?: string;
  setNeedUpdateFields?: (value: boolean) => void;
}

const SIGN_UP_TEST_ID = EDataTestId.LOGIN_ACCOUNT_SIGNUP;

const AccountSignUp: FC<IProps> = ({ selectedTab, registrationUrl, setNeedUpdateFields }) => {
  const { t } = useTranslation();
  const { company } = useCompany();

  const validRegistrationUrl = !!registrationUrl;
  const showForm = validRegistrationUrl && registrationUrl?.toLocaleLowerCase() === 'form';
  const isOnDemoTab = DEMO_LOGINS.includes(selectedTab);
  const { hasDemoAccountCreation } = useMemo(() => {
    return validateCompanyAccess(company);
  }, [company]);

  const openRegistrationURL = useCallback(() => {
    if (registrationUrl) {
      openInAppBrowser(registrationUrl);
    }
  }, [registrationUrl]);

  const goToSignUpScreen = () => {
    navigate(ALL_MOBILE_ROUTES.AUTH.SIGN_UP);
  };

  const goToDemoCreationScreen = () => {
    navigate(ALL_MOBILE_ROUTES.AUTH.DEMO_CREATION, { publicAuth: true, setNeedUpdateFields: setNeedUpdateFields });
  };

  if (isOnDemoTab && hasDemoAccountCreation) {
    return (
      <SignUp>
        <SignUpLink isLink={false} onPress={() => goToDemoCreationScreen()} testID={SIGN_UP_TEST_ID}>
          {t('Open a Demo Account')}
        </SignUpLink>
      </SignUp>
    );
  }

  if (!validRegistrationUrl) {
    return null;
  }

  return (
    <SignUp>
      <SignUpDescription>{t(`Don't have an account?`)}</SignUpDescription>
      <SignUpLink
        isLink={!showForm}
        onPress={showForm ? goToSignUpScreen : openRegistrationURL}
        testID={SIGN_UP_TEST_ID}
      >
        {t('Sign up')}
      </SignUpLink>
    </SignUp>
  );
};

export default AccountSignUp;

const SignUp = styled.View`
  flex-direction: row;
  justify-content: center;
`;

const SignUpDescription = styled(Text)`
  font-size: ${dynamicSize.convertSize(14)}px;
  margin-end: ${dynamicSize.convertSize(6)}px;
`;

const SignUpLink = styled(Text)<{ isLink: boolean }>`
  font-size: ${dynamicSize.convertSize(14)}px;
  text-decoration: ${({ isLink }) => (isLink ? 'underline' : 'none')};
  text-decoration-color: ${({ theme, isLink }) => (isLink ? theme.colors.primary : theme.colors.transparent)};
  color: ${({ theme }) => theme.colors.primary};
`;
